import { useState } from "react";
import EmptyOrders from "../EmptyOrders";
import OrderDetailsDialog from "../../dialogs/OrderDetails";
import CancelUserOrderConfirmDialog from "../../dialogs/CancelUserOrderConfirmDialog";
import { Box } from "@mui/system";
import { Button, Card, CardActions, CardContent, Divider, Typography } from "@mui/material";
import { Colors } from "../../helpers/Consts";
import { getDataExtensoSimples } from "../../helpers/Utils";
import OrderItemDetails from "./orderItemDetails";
import { IShoppingBagData } from "../../types/ShoppingBag";

type Props = {
  parcialItensNotFiltered: IShoppingBagData[] | undefined;
};

/**
 * Exibe os detalhes de pedido, APENAS para pedidos do tipo MESA.
 * Para pedidos do tipo ENTREGA ou RETIRADA, veja SingleOrderCard.
 *
 * @param parcialItensNotFiltered
 * @constructor
 */
function TableOrderDetailsCard({ parcialItensNotFiltered }: Props) {

  const [open, setOpen] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

  const handleClose = () => {
    setOpen(!open);
  };
  const handleCancelOrder = () => {
    setOpenConfirmDialog(!openConfirmDialog);
  };

  function getPriceSubtotal(): number {
    return parcialItens.reduce(
      (accumulator, obj) => {
        return accumulator + obj.subtotal;
      },
      0,
    );
  }

  function getPriceTotal(): number {
    return parcialItens.reduce(
      (accumulator, obj) => {
        return accumulator + obj.total;
      },
      0,
    );
  }

  function getPriceDiscount(): number {
    return parcialItens.reduce(
      (accumulator, obj) => {
        return accumulator + obj.discount_value;
      },
      0,
    );
  }

  if (!parcialItensNotFiltered || !parcialItensNotFiltered.filter) {
    return (<EmptyOrders />);
  }

  const parcialItens = parcialItensNotFiltered.filter(
    (pedido) => pedido.items.length > 0,
  );

  if (parcialItens.length < 1) {
    return (<EmptyOrders />);
  }

  return (
    <>
      <OrderDetailsDialog order={parcialItens[0]} open={open} handleClose={handleClose} />
      <CancelUserOrderConfirmDialog
        open={openConfirmDialog}
        order={parcialItens[0]}
        handleCloseDialog={handleCancelOrder}
      />
      <Box
        sx={{
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{ fontSize: "0.7rem" }}
          fontWeight="700"
          color={Colors.baseTextColor}
        >
          {getDataExtensoSimples(
            new Date(parcialItens[0].created_at).toLocaleDateString("pt-Br"),
          )}
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex" }}>
              <Typography
                fontSize="0.7rem"
                color={Colors.baseTextColor}
                fontWeight="300"
              >
                Pedido #
              </Typography>
              <Typography
                fontSize="0.7rem"
                color={Colors.grayTextColorDarkestDark}
                fontWeight="700"
              >
                {parcialItens[0].id}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography fontSize="0.6rem" color={Colors.grayTextColorDarkest}>
                {new Date(parcialItens[0].created_at).toLocaleString("pt-Br")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Typography
                fontSize="0.6rem"
                color={parcialItens[0].status !== 13 ? Colors.orangePrimary : "red"}
                fontWeight="700"
              >
                {parcialItens[0].status_description === "Criado"
                  ? "Sacola em Aberto"
                  : parcialItens[0].status_description}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography
                fontSize="0.6rem"
                color={parcialItens[0].status !== 13 ? Colors.orangePrimary : "red"}
              >
                {parcialItens[0].status === 13
                  ? new Date(parcialItens[0].cancelled_at).toLocaleString("pt-Br")
                  : new Date(parcialItens[0].updated_at).toLocaleString("pt-Br")}
              </Typography>
            </Box>
            <Divider sx={{ padding: "5px" }} />
            <Box paddingTop={"15px"} display={"flex"}>
              <Box
                component="img"
                sx={{ borderRadius: "50%", height: "50px" }}
                src={parcialItens[0].placeInfo.logo_image}
                alt="alt"
              />
              <Box p={1} marginLeft={"10px"}>
                <Typography
                  fontSize="0.8rem"
                  fontWeight={"700"}
                  color={Colors.grayTextColorDarkestDark}
                >
                  {parcialItens[0].placeInfo.name}
                </Typography>
                <Typography fontSize="0.6rem" color={Colors.baseTextColor}>
                  {parcialItens[0].placeInfo.short_description}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ padding: "5px" }} />
            <Box padding={"10px 0px"}>
              {parcialItens.map(pedido => (
                pedido.items.map((item) => (
                  <Box
                    sx={{
                      marginBottom: "6px",
                    }}
                    key={item.id}
                  >
                    <OrderItemDetails singleItem={item} />
                  </Box>
                ))

              ))}
            </Box>
            <Divider sx={{ padding: "5px" }} />
            <Box paddingTop="8px">
              <Box display={"flex"}>
                <Typography
                  fontSize="0.7rem"
                  color={Colors.baseTextColor}
                >
                  Valor dos Itens
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography
                  fontSize="0.7rem"
                  fontWeight={700}
                  color={Colors.grayTextColorDarkestDark}
                >
                  {getPriceSubtotal().toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Typography>
              </Box>
            </Box>
            <Box paddingTop="2px">
              <Box display={"flex"}>
                <Typography
                  fontSize="0.7rem"
                  color={Colors.baseTextColor}
                >
                  Desconto
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography
                  fontSize="0.7rem"
                  fontWeight={700}
                  color={Colors.grayTextColorDarkestDark}
                >
                  {getPriceDiscount().toLocaleString(
                    "pt-br",
                    {
                      style: "currency",
                      currency: "BRL",
                    },
                  )}
                </Typography>
              </Box>
            </Box>
            <Box paddingTop="2px">
              <Box display={"flex"}>
                <Typography
                  fontSize="0.7rem"
                  color={Colors.baseTextColor}
                >
                  Total a Pagar
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography
                  fontSize="0.7rem"
                  fontWeight={700}
                  color={Colors.grayTextColorDarkestDark}
                >
                  {getPriceTotal().toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Typography>
              </Box>
            </Box>
          </CardContent>
          <CardActions>
            {parcialItens[0].status !== 1 && (
              <Box sx={{ width: "100%" }}>
                <Button
                  size="small"
                  sx={{
                    width: "50%",
                    color: "red",
                    textTransform: "none",
                  }}
                  disabled={
                    parcialItens[0].status === 13 ||
                    parcialItens[0].status === 3 ||
                    parcialItens[0].status === 4 ||
                    parcialItens[0].status === 11 ||
                    parcialItens[0].status === 10 ||
                    parcialItens[0].status === 8 ||
                    parcialItens[0].status === 9 ||
                    parcialItens[0].status === 7
                  }
                  onClick={() => {
                    handleCancelOrder();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  sx={{
                    width: "50%",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    handleClose();
                  }}
                  disabled
                >
                  Detalhes
                </Button>
              </Box>
            )}
          </CardActions>
        </Card>
      </Box>
    </>
  );
}

export default TableOrderDetailsCard;